import { useEffect, useRef, useState } from 'react';
import { UserSettings } from '../api';

const isValid = (settings: UserSettings) =>
  (!settings.sendNotificationsToTelegram || !settings.telegramId || /^\d+$/.test(settings.telegramId)) &&
  (!settings.sendNotificationsToEmail || !settings.email || /^[^@]+@.+$/.test(settings.email));

/** Owned by App so dismissing Settings cannot discard a draft or an in-flight save. */
export function useSettingsAutosave(
  settings: UserSettings,
  persist: (settings: UserSettings) => Promise<UserSettings>,
  onSaved: (settings: UserSettings) => void,
) {
  const [draft, setDraft] = useState<UserSettings | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const current = useRef<UserSettings | null>(null);
  const pending = useRef<UserSettings | null>(null);
  const running = useRef<Promise<boolean> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revision = useRef(0);
  const generation = useRef(0);

  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current);
    generation.current += 1;
  }, []);

  const cancelTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const flush = (): Promise<boolean> => {
    cancelTimer();
    if (running.current) return running.current;
    if (!pending.current) return Promise.resolve(current.current === null);
    const owner = generation.current;
    // Defer the loop until running.current is assigned, including synchronous failures.
    const operation = Promise.resolve().then(async () => {
      while (pending.current && generation.current === owner) {
        const snapshot = pending.current;
        pending.current = null;
        if (!isValid(snapshot)) {
          setStatus('idle');
          return false;
        }
        setStatus('saving');
        setError(null);
        try {
          const updated = await persist(snapshot);
          if (generation.current !== owner) return false;
          onSaved(updated);
          if (current.current === snapshot) {
            current.current = null;
            setDraft(null);
            setStatus('saved');
          }
        } catch (failure) {
          if (generation.current !== owner) return false;
          // Leave the latest draft available for an explicit retry; never loop on failure.
          cancelTimer();
          pending.current = null;
          setError(failure instanceof Error ? failure.message : 'Failed to update settings');
          setStatus('error');
          return false;
        }
      }
      return current.current === null;
    }).finally(() => {
      if (generation.current === owner) running.current = null;
    });
    running.current = operation;
    return operation;
  };

  const change = (patch: Partial<UserSettings>, debounce = false) => {
    const next = { ...(current.current ?? settings), ...patch };
    next.telegramId = next.telegramId?.trim() || null;
    next.email = next.email?.trim() || null;
    if (JSON.stringify(next) === JSON.stringify(current.current ?? settings)) return;
    revision.current += 1;
    current.current = next;
    setDraft(next);
    setError(null);
    setStatus('saving');
    cancelTimer();
    // A text edit stays out of the queue until its debounce expires, even if a request is running.
    pending.current = null;
    const enqueue = () => {
      timer.current = null;
      pending.current = current.current;
      void flush();
    };
    if (debounce) timer.current = setTimeout(enqueue, 400);
    else enqueue();
  };

  const flushDraft = () => {
    if (timer.current !== null) pending.current = current.current;
    return flush();
  };

  const retry = () => {
    pending.current = current.current;
    return flush();
  };

  const reset = () => {
    generation.current += 1;
    revision.current += 1;
    cancelTimer();
    current.current = null;
    pending.current = null;
    running.current = null;
    setDraft(null);
    setError(null);
    setStatus('idle');
  };

  return {
    settings: draft ?? settings, status, error, change, flush: flushDraft, retry, reset,
    getRevision: () => revision.current,
    hasDraft: () => current.current !== null,
  };
}
